import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert
} from '@mui/material';
import {
  Category as CategoryIcon,
  Warehouse as WarehouseIcon,
  Room as ZoneIcon
} from '@mui/icons-material';

// Import the management components
import CategoryManager from '../components/CategoryManager';
import { useAuth } from '../contexts/AuthContext';

/**
 * TabPanel component for displaying tab content
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setup-tabpanel-${index}`}
      aria-labelledby={`setup-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Setup Page Component
 * 
 * This page provides an interface for managers and administrators to
 * configure system settings like categories, warehouses, and zones.
 */
const SetupPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();
  
  // Check if user has setup permissions
  const canManageSetup = user?.roles?.includes('admin') || user?.roles?.includes('manager');
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // If user doesn't have permissions, show access denied message
  if (!canManageSetup) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            You do not have permission to access the setup panel. 
            Please contact an administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        System Setup
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure categories, warehouses, and zones to organize your inventory system.
      </Typography>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<CategoryIcon />} label="Categories" />
          <Tab icon={<WarehouseIcon />} label="Warehouses" />
          <Tab icon={<ZoneIcon />} label="Zones" />
        </Tabs>
      </Paper>
      
      <TabPanel value={activeTab} index={0}>
        <CategoryManager />
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <Typography variant="h5" gutterBottom>
          Warehouse Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Manage your physical warehouse locations and their properties.
        </Typography>
        <Alert severity="info">
          Warehouse management functionality will be implemented in a future update.
          Currently, warehouses can be configured in the inventory system directly.
        </Alert>
        {/* WarehouseManager component would go here */}
      </TabPanel>
      
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5" gutterBottom>
          Zone Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Configure zones within your warehouses for better organization.
        </Typography>
        <Alert severity="info">
          Zone management functionality will be implemented in a future update.
          Currently, zones can be managed within individual warehouse settings.
        </Alert>
        {/* ZoneManager component would go here */}
      </TabPanel>
    </Container>
  );
};

export default SetupPage;